/**
 * ═══════════════════════════════════════════════════════════════
 *  PERMISSION MAP — Metadata for ALL gated actions
 * ═══════════════════════════════════════════════════════════════
 *
 * Action ID strings are defined in actionIds.js (single source of truth).
 * This file maps each action ID to its metadata (area, label, feature, defaultType).
 *
 * defaultType controls behavior when NO community override exists in Firestore:
 *   "public"     → anyone in the community can use it
 *   "restricted" → only committee members with the relevant area
 *   "hidden"     → invisible to unauthorized users
 *
 * HOW TO ADD A NEW GATED ACTION:
 *   1. Add the constant in actionIds.js
 *   2. Add a metadata entry here using that constant as the key
 *   3. Use ACTION_IDS.YOUR_CONSTANT in <GatedAction> or useAction()
 */

import A, { ACTION_TYPES } from "./actionIds";

const { PUBLIC, RESTRICTED, HIDDEN } = ACTION_TYPES;

const PERMISSION_MAP = {
  // ── FINANCE ─────────────────────────────────────────────────
  [A.FINANCE_ACCOUNT_CREATE]: {
    area: "finance",
    label: "Create account head",
    feature: "Accounts",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_ACCOUNT_RECORD_ENTRY]: {
    area: "finance",
    label: "Record a manual collection/expense entry",
    feature: "Account Details",
    defaultType: PUBLIC,
  },
  [A.FINANCE_PAYMENT_APPROVE]: {
    area: "finance",
    label: "Approve a payment claim",
    feature: "Collections",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_PAYMENT_REJECT]: {
    area: "finance",
    label: "Reject a payment claim",
    feature: "Collections",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_ACCOUNT_TRANSFER]: {
    area: "finance",
    label: "Transfer balance to primary account",
    feature: "Account Details",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_ACCOUNT_ACKNOWLEDGE]: {
    area: "finance",
    label: "Acknowledge a received transaction",
    feature: "Account Details",
    defaultType: PUBLIC,
  },
  [A.FINANCE_ACCOUNT_CLOSE]: {
    area: "finance",
    label: "Close an account head",
    feature: "Account Details",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_ACCOUNT_DELETE]: {
    area: "finance",
    label: "Delete an account head",
    feature: "Account Details",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_PAY_COMPONENT_EDIT]: {
    area: "finance",
    label: "Edit a pay component",
    feature: "Pay Components",
    defaultType: PUBLIC,
  },
  [A.FINANCE_PAY_COMPONENT_DELETE]: {
    area: "finance",
    label: "Delete a pay component",
    feature: "Pay Components",
    defaultType: PUBLIC,
  },
  [A.FINANCE_RECORD_CHEQUE_DEBIT]: {
    area: "finance",
    label: "Record Cheque / Debit",
    feature: "Project Journey",
    defaultType: PUBLIC,
  },
  [A.FINANCE_BILLING_INPUT_MANAGE]: {
    area: "finance",
    label: "Manage variable billing inputs",
    feature: "Pay Components",
    defaultType: RESTRICTED,
  },
  [A.FINANCE_BILL_HOLD]: {
    area: "finance",
    label: "View billing hold status",
    feature: "Pay Components",
    defaultType: RESTRICTED,
  },

  // ── DEVELOPMENT ──────────────────────────────────────────────
  [A.DEVELOPMENT_CREATE]: {
    area: "development",
    label: "Create a new project",
    feature: "Development",
    defaultType: PUBLIC,
  },

  // ── VENDORS ─────────────────────────────────────────────────
  [A.VENDORS_VENDOR_ADD]: {
    area: "vendors",
    label: "Add a new vendor",
    feature: "Vendors",
    defaultType: PUBLIC,
  },
  [A.VENDORS_VENDOR_EDIT]: {
    area: "vendors",
    label: "Edit vendor details",
    feature: "Vendor Detail",
    defaultType: PUBLIC,
  },
  [A.VENDORS_VENDOR_DEACTIVATE]: {
    area: "vendors",
    label: "Deactivate a vendor",
    feature: "Vendor Detail",
    defaultType: RESTRICTED,
  },
  [A.VENDORS_CONTRACT_ADD]: {
    area: "vendors",
    label: "Add a new contract",
    feature: "Vendor Detail",
    defaultType: PUBLIC,
  },

  // ── COMMUNITY ───────────────────────────────────────────────
  [A.COMMUNITY_STRUCTURE_ADD]: {
    area: "community",
    label: "Add block / floor / flat",
    feature: "Community Structure",
    defaultType: PUBLIC,
  },
  [A.COMMUNITY_MEMBER_ASSIGN]: {
    area: "community",
    label: "Assign member to a flat",
    feature: "Community Members",
    defaultType: RESTRICTED,
  },
  [A.COMMUNITY_DATA_CLEAR]: {
    area: "community",
    label: "Clear all community data",
    feature: "Community Settings",
    defaultType: HIDDEN,
  },

  // ── INVITATIONS ─────────────────────────────────────────────
  [A.INVITATIONS_MEETING_CREATE]: {
    area: "invitations",
    label: "Create a new meeting",
    feature: "Meetings",
    defaultType: PUBLIC,
  },
  [A.INVITATIONS_MEETING_SAVE]: {
    area: "invitations",
    label: "Save meeting draft",
    feature: "Meeting Editor",
    defaultType: PUBLIC,
  },
  [A.INVITATIONS_MEETING_ACTIVATE]: {
    area: "invitations",
    label: "Make a meeting active",
    feature: "Meeting Editor",
    defaultType: PUBLIC,
  },
  [A.INVITATIONS_MEETING_CONCLUDE]: {
    area: "invitations",
    label: "Conclude a meeting",
    feature: "Meeting Editor",
    defaultType: PUBLIC,
  },
  [A.INVITATIONS_MEETING_DELETE]: {
    area: "invitations",
    label: "Delete a meeting",
    feature: "Meeting Editor",
    defaultType: PUBLIC,
  },

  // ── VISITORS ────────────────────────────────────────────────
  [A.VISITORS_SECURITY_CODE_CREATE]: {
    area: "visitors",
    label: "Create a security gate access code",
    feature: "Visitor Management",
    defaultType: PUBLIC,
  },
  [A.VISITORS_SECURITY_CODE_DELETE]: {
    area: "visitors",
    label: "Delete a security gate access code",
    feature: "Visitor Management",
    defaultType: PUBLIC,
  },
  [A.VISITORS_ADD]: {
    area: "visitors",
    label: "Add a visitor entry",
    feature: "Visitor Management",
    defaultType: PUBLIC,
  },
  [A.VISITORS_CHECK_IN]: {
    area: "visitors",
    label: "Check in a visitor",
    feature: "Visitor Management",
    defaultType: PUBLIC,
  },
  [A.VISITORS_CHECK_OUT]: {
    area: "visitors",
    label: "Check out a visitor",
    feature: "Visitor Management",
    defaultType: PUBLIC,
  },
  [A.VISITORS_CANCEL]: {
    area: "visitors",
    label: "Cancel a visitor entry",
    feature: "Visitor Management",
    defaultType: PUBLIC,
  },
  [A.VISITORS_DELETE]: {
    area: "visitors",
    label: "Delete a visitor record",
    feature: "Visitor Management",
    defaultType: RESTRICTED,
  },

  // ── SOCIAL ──────────────────────────────────────────────────
  [A.SOCIAL_POST_CREATE]: {
    area: "community",
    label: "Create a social post",
    feature: "Social Hub",
    defaultType: PUBLIC,
  },
  [A.SOCIAL_POST_DELETE_ANY]: {
    area: "community",
    label: "Delete any member's social post",
    feature: "Social Hub",
    defaultType: RESTRICTED,
  },
  [A.SOCIAL_SKILL_CREATE]: {
    area: "community",
    label: "Add a skill listing",
    feature: "Social Hub",
    defaultType: PUBLIC,
  },
  [A.SOCIAL_COMMUNITY_PROFILE_EDIT]: {
    area: "community",
    label: "Edit community social profile",
    feature: "Social Hub",
    defaultType: PUBLIC,
  },
  [A.SOCIAL_COMMUNITY_FOLLOW]: {
    area: "community",
    label: "Follow / unfollow communities",
    feature: "Social Hub",
    defaultType: PUBLIC,
  },

  // ── AMENITY BOOKING ─────────────────────────────────────────
  [A.AMENITY_CREATE]: {
    area: "amenity",
    label: "Add a new amenity",
    feature: "Amenity Booking",
    defaultType: RESTRICTED,
  },
  [A.AMENITY_EDIT]: {
    area: "amenity",
    label: "Edit amenity settings",
    feature: "Amenity Booking",
    defaultType: RESTRICTED,
  },
  [A.AMENITY_DELETE]: {
    area: "amenity",
    label: "Delete an amenity",
    feature: "Amenity Booking",
    defaultType: RESTRICTED,
  },
  [A.AMENITY_BOOKING_CANCEL]: {
    area: "amenity",
    label: "Cancel a booking",
    feature: "Amenity Booking",
    defaultType: PUBLIC,
  },

  // ── DAILY HELP ──────────────────────────────────────────────
  [A.DAILY_HELP_CREATE]: {
    area: "community",
    label: "Add a helper listing",
    feature: "Daily Help",
    defaultType: PUBLIC,
  },
  [A.DAILY_HELP_DELETE]: {
    area: "community",
    label: "Delete a helper listing",
    feature: "Daily Help",
    defaultType: PUBLIC,
  },

  // ── DOCUMENTS ───────────────────────────────────────────────
  [A.DOCUMENTS_CREATE]: {
    area: "documents",
    label: "Upload a new document",
    feature: "Documents",
    defaultType: PUBLIC,
  },
  [A.DOCUMENTS_EDIT]: {
    area: "documents",
    label: "Edit a document",
    feature: "Documents",
    defaultType: PUBLIC,
  },
  [A.DOCUMENTS_DELETE]: {
    area: "documents",
    label: "Delete a document",
    feature: "Documents",
    defaultType: RESTRICTED,
  },

  // ── EMERGENCY CONTACTS ──────────────────────────────────────
  [A.EMERGENCY_CONTACT_CREATE]: {
    area: "community",
    label: "Add an emergency contact",
    feature: "Emergency Contacts",
    defaultType: PUBLIC,
  },
  [A.EMERGENCY_CONTACT_EDIT]: {
    area: "community",
    label: "Edit an emergency contact",
    feature: "Emergency Contacts",
    defaultType: PUBLIC,
  },
  [A.EMERGENCY_CONTACT_DELETE]: {
    area: "community",
    label: "Delete an emergency contact",
    feature: "Emergency Contacts",
    defaultType: RESTRICTED,
  },

  // ── NOTICES ─────────────────────────────────────────────────
  [A.NOTICES_CREATE]: {
    area: "community",
    label: "Post a notice",
    feature: "Notices",
    defaultType: PUBLIC,
  },
  [A.NOTICES_PIN]: {
    area: "community",
    label: "Pin / unpin a notice",
    feature: "Notices",
    defaultType: RESTRICTED,
  },
  [A.NOTICES_DELETE]: {
    area: "community",
    label: "Delete a notice",
    feature: "Notices",
    defaultType: RESTRICTED,
  },

  // ── POLLS ───────────────────────────────────────────────────
  [A.POLLS_CREATE]: {
    area: "community",
    label: "Create a poll",
    feature: "Polls",
    defaultType: PUBLIC,
  },
  [A.POLLS_CLOSE]: {
    area: "community",
    label: "Close / reopen a poll",
    feature: "Polls",
    defaultType: PUBLIC,
  },
  [A.POLLS_DELETE]: {
    area: "community",
    label: "Delete a poll",
    feature: "Polls",
    defaultType: RESTRICTED,
  },

  // ── ENGAGEMENT — STORY WALL ─────────────────────────────────
  [A.ENGAGEMENT_STORY_CREATE]: {
    area: "engagement",
    label: "Post a story",
    feature: "Story Wall",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_STORY_DELETE]: {
    area: "engagement",
    label: "Delete a story",
    feature: "Story Wall",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_STORY_REACT]: {
    area: "engagement",
    label: "React to a story",
    feature: "Story Wall",
    defaultType: PUBLIC,
  },

  // ── ENGAGEMENT — OFFERINGS ──────────────────────────────────
  [A.ENGAGEMENT_OFFERING_CREATE]: {
    area: "engagement",
    label: "Post an offering",
    feature: "Offerings",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_OFFERING_CLAIM]: {
    area: "engagement",
    label: "Claim an offering",
    feature: "Offerings",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_OFFERING_FULFILL]: {
    area: "engagement",
    label: "Mark offering as fulfilled",
    feature: "Offerings",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_OFFERING_DELETE]: {
    area: "engagement",
    label: "Delete an offering",
    feature: "Offerings",
    defaultType: PUBLIC,
  },

  // ── ENGAGEMENT — BUDDY GROUPS ───────────────────────────────
  [A.ENGAGEMENT_BUDDY_CREATE]: {
    area: "engagement",
    label: "Create a buddy group",
    feature: "Buddy Groups",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_BUDDY_JOIN]: {
    area: "engagement",
    label: "Join a buddy group",
    feature: "Buddy Groups",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_BUDDY_POST]: {
    area: "engagement",
    label: "Post in a buddy group",
    feature: "Buddy Groups",
    defaultType: PUBLIC,
  },

  // ── ENGAGEMENT — PUZZLES & GAMES ────────────────────────────
  [A.ENGAGEMENT_PUZZLE_PLAY]: {
    area: "engagement",
    label: "Play a daily puzzle",
    feature: "Daily Puzzles",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_GAME_CREATE]: {
    area: "engagement",
    label: "Challenge someone to a game",
    feature: "Games",
    defaultType: PUBLIC,
  },
  [A.ENGAGEMENT_GAME_PLAY]: {
    area: "engagement",
    label: "Play a game move",
    feature: "Games",
    defaultType: PUBLIC,
  },

  // ── ENGAGEMENT — CONTRIBUTIONS ───────────────────────────────
  [A.ENGAGEMENT_CONTRIBUTE]: {
    area: "engagement",
    label: "Submit community content",
    feature: "Contribute",
    defaultType: PUBLIC,
  },

};

export default PERMISSION_MAP;

/**
 * Helper: get area for an action ID.
 * Returns null if the action ID is not registered (should never happen).
 */
export function getAreaForAction(actionId) {
  const entry = PERMISSION_MAP[actionId];
  if (!entry) {
    console.error(
      `[PermissionMap] Unknown actionId: "${actionId}". ` +
      `Add it to src/Shared/permissionMap.js before using it.`
    );
    return null;
  }
  return entry.area;
}

/**
 * Get the default type for an action. Returns "public" if not found.
 */
export function getDefaultType(actionId) {
  return PERMISSION_MAP[actionId]?.defaultType || "public";
}

/**
 * Dev audit helper — prints a summary of all registered actions grouped by area.
 * Call from browser console: window.__auditPermissions()
 */
export function auditPermissions() {
  const grouped = {};
  Object.entries(PERMISSION_MAP).forEach(([key, val]) => {
    if (!grouped[val.area]) grouped[val.area] = [];
    grouped[val.area].push({ actionId: key, ...val });
  });
  console.table(
    Object.entries(PERMISSION_MAP).map(([key, val]) => ({
      actionId: key,
      area: val.area,
      feature: val.feature,
      label: val.label,
    }))
  );
  return grouped;
}

// Expose audit helper in dev mode
if (process.env.NODE_ENV === "development") {
  window.__auditPermissions = auditPermissions;
}
